import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import ReplyIcon from "@mui/icons-material/Reply";
import RestoreIcon from "@mui/icons-material/Restore";
import { Box, IconButton, ImageList, Stack, Tooltip, Typography } from "@mui/material";
import downloadjs from "downloadjs";
import React, { forwardRef, useState } from "react";
import { AppUtils } from "source/framework/appUtils";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { FileType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import * as attachmentService from "../../../../models/services/attachmentService";
import { NewMessageAttachmentImage } from "../NewMessage/NewMessageAttachmentImage";
import { NewMessageAttachmentItem } from "../NewMessage/NewMessageAttachmentItem";
import * as Styled from "./MessageLine.styles";
import { RemoveToolButton } from "../../Button/RemoveToolButton";

export interface IMessageListLineProp {
  id: string;
  userCode?: string;
  content: string;
  sender: string;
  date: number;
  myMessage: boolean;
  attachments?: IFileAttachment[];
  someRead?: boolean;
  allRead?: boolean;
  isLate?: boolean;
  isDeleted?: boolean;
  isNew?: boolean;
  image?: string;
  showAvatar?: boolean;
  readTooltip?: string;
  senderDisabled?: boolean;
  senderTooltip?: string;
  replyText?: string;
  replySender?: string;
  onClick?: (id: string) => void;
  onRemove?: () => void;
  onReply?: () => void;
  onRestore?: () => void;
}

const MessageListLine = forwardRef((
    props: IMessageListLineProp,
    ref
) => {
    const imageAttachments = props.attachments?.filter((i) => i.type === FileType.Image);
    const fileAttachments = props.attachments?.filter((i) => i.type !== FileType.Image);
    const [hover, setHover] = useState(false);

    const handleAttachmentClick = async (id: string) => {
        if (!id) return;
        const attachment = props.attachments.find((i) => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(
            String.format(Translations.AttachmentParameterDownloaded, fileName)
        );
    };

    return (
        <Box
            className={`id${props.id}`}
            sx={{
                display: "flex",
                flexDirection: props.myMessage ? "row-reverse" : "row",
                justifyContent: "flex-start",
                margin: ".5rem 0",
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {props.showAvatar &&
                <AvatarImage
                    id={props.userCode}
                    name={props.sender}
                    image={props.image}
                    tooltip={props.sender}
                    size={38}
                    marginLeft={props.myMessage ? ".5rem" : undefined}
                    marginRight={!props.myMessage ? ".5rem" : undefined}
                />
            }
            <Styled.Message
                message={props.myMessage ? "true" : "false"}
                ref={ref}
                data-id={props.id}
                sx={{ maxWidth: "50%" }}
            >
                <Styled.MessageBox
                    message={props.myMessage ? "true" : "false"}
                    display="flex"
                    flexDirection="column"
                    onClick={(e) => {
                        e.stopPropagation();
                        props.onClick(props.id);
                    }}
                >
                    {props.myMessage ? <Styled.MessageTipRight color="rgba(255, 255, 255, 0.16)" /> : <Styled.MessageTipLeft />}
                    {!props.isDeleted && imageAttachments?.length > 0 && (
                        <ImageList cols={imageAttachments?.length === 1 ? 1 : 2} variant="quilted">
                            {imageAttachments.map((attachment) => (
                                <NewMessageAttachmentImage
                                    key={attachment.id}
                                    attachment={attachment}
                                    onClick={handleAttachmentClick}
                                />
                            ))}
                        </ImageList>
                    )}
                    {!props.isDeleted && fileAttachments?.length > 0 && (
                        <div className="wbAttachments">
                            {fileAttachments.map((attachment) => (
                                <NewMessageAttachmentItem
                                    key={attachment.id}
                                    attachment={attachment}
                                    onClick={handleAttachmentClick}
                                />
                            ))}
                        </div>
                    )}
                    {props.replyText &&
                        <Box
                            sx={(theme) => ({
                                bgcolor: theme.palette.background.paper,
                                borderLeft: ".25rem solid",
                                borderColor: "success.light",
                                borderRadius: "4px 4px 0 0",
                                padding: ".25rem .75rem",
                                marginBottom: ".25rem",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            })}
                        >
                            <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{props.replySender}</Typography>
                                <Typography dangerouslySetInnerHTML={{ __html: Base.linkifyText(props.replyText) }} />
                            </Box>
                        </Box>
                    }
                    <Typography
                        sx={{ fontStyle: props.isDeleted ? "italic" : undefined, color: props.isDeleted ? "text.secondary" : undefined }}
                        dangerouslySetInnerHTML={{ __html: Base.linkifyText(props.content) }}
                    />
                    <Box display="flex" justifyContent="flex-end" sx={{ marginTop: ".25rem", color: "text.secondary" }}>
                        <Tooltip title={props.senderTooltip}>
                            <Typography variant="caption" sx={{ color: "text.primary", textDecoration: props.senderDisabled ? "line-through" : undefined }}>
                                {props.sender}
                            </Typography>
                        </Tooltip>
                        <Typography variant="caption" textAlign="right">
                            {` - ${Base.timeToDateTimeStr(props.date)}`}
                        </Typography>
                        {props.myMessage && (props.someRead || props.allRead) && (
                            <Tooltip
                                title={
                                    props.readTooltip ||
                                    (props.allRead
                                        ? Translations.AllWhistleBlowHandlersHaveReadMessage
                                        : Translations.SomeWhistleBlowHandlersHaveReadMessage)
                                }
                            >
                                {props.allRead
                                    ? (
                                        <DoneAllOutlinedIcon
                                            fontSize="small"
                                            color="info"
                                            sx={{ marginLeft: ".25rem", marginRight: "-0.25rem" }}
                                        />
                                    )
                                    : (
                                        <DoneOutlinedIcon
                                            fontSize="small"
                                            color="info"
                                            sx={{ marginLeft: ".25rem", marginRight: "-0.25rem" }}
                                        />
                                    )}
                            </Tooltip>
                        )}
                        {props.isNew &&
                            <Tooltip title={Translations.NewMessage}>
                                <NewReleasesIcon fontSize="small" color="warning" sx={{ marginLeft: ".25rem", marginRight: "-0.25rem" }} />
                            </Tooltip>
                        }
                    </Box>
                </Styled.MessageBox>
            </Styled.Message>
            {(props.onRemove || props.onReply) && hover &&
                <Stack
                    direction="row"
                    sx={{
                        backgroundColor: "background.paper",
                        borderRadius: "20px",
                        border: "1px solid",
                        borderColor: "divider",
                        alignSelf: "center",
                        margin: "0 1rem"
                    }}
                >
                    {props.onRemove &&
                        <RemoveToolButton onClick={props.onRemove} />
                    }
                    {props.onRestore &&
                        <Tooltip title={Translations.Restore}>
                            <IconButton onClick={props.onRestore} color="info"><RestoreIcon /></IconButton>
                        </Tooltip>
                    }
                    {props.onReply &&
                        <Tooltip title={Translations.ReplyToInstantMessage}>
                            <IconButton onClick={props.onReply}><ReplyIcon /></IconButton>
                        </Tooltip>
                    }
                </Stack>
            }
        </Box>

    );
});

MessageListLine.displayName = "MessageListLine";

export default MessageListLine;
